import { createStore } from "zustand";
import { createBoundedUseStore } from "@graphiql/react";
import { z } from "zod";
import { randomId } from "./utility/uuid.js";
import { DEFAULT_COLLECTIONS_CONFIG } from "./types.js";
import { localStorageAdapter } from "./storage/local-storage.js";
import { saveTabLinks, loadTabLinks } from "./storage/tab-links.js";
const INITIAL_SAVE_DIALOG = {
  open: false,
  query: "",
  variables: "",
  headers: "",
  name: ""
};
function deriveOperationName(query, fallback) {
  if (fallback) {
    return fallback;
  }
  const match = /(?:query|mutation|subscription)\s+([A-Za-z_][A-Za-z0-9_]*)/i.exec(query);
  return (match == null ? void 0 : match[1]) ?? "Unnamed operation";
}
function pruneLinksForItems(links, itemIds) {
  const next = {};
  for (const [tabId, link] of Object.entries(links)) {
    if (!itemIds.has(link.itemId)) {
      next[tabId] = link;
    }
  }
  return next;
}
const norm = (v) => v ?? "";
function itemContentEqual(a, b) {
  return a.name === b.name && norm(a.query) === norm(b.query) && norm(a.variables) === norm(b.variables) && norm(a.headers) === norm(b.headers);
}
const collectionsStore = createStore((set, get) => {
  const persist = async () => {
    await get().storage.save(get().collections);
  };
  return {
    collections: [],
    loaded: false,
    storage: localStorageAdapter,
    config: DEFAULT_COLLECTIONS_CONFIG,
    links: {},
    saveDialog: INITIAL_SAVE_DIALOG,
    actions: {
      async init(storage, config) {
        const collections = await storage.load();
        set({
          collections,
          loaded: true,
          storage,
          config: { ...DEFAULT_COLLECTIONS_CONFIG, ...config },
          // Restore tab↔item links so ⌘S on a tab reopened from the previous
          // session still updates its saved operation in place.
          links: loadTabLinks()
        });
      },
      setCollections(collections) {
        set({ collections });
      },
      async reload() {
        const collections = await get().storage.load();
        set({ collections });
      },
      createCollection(name, description) {
        const c = {
          id: randomId(),
          name,
          description,
          items: [],
          createdAt: Date.now(),
          updatedAt: Date.now()
        };
        set((s) => ({ collections: [...s.collections, c] }));
        void persist();
        return c;
      },
      deleteCollection(id) {
        const removed = get().collections.find((c) => c.id === id);
        const removedItemIds = new Set((removed == null ? void 0 : removed.items.map((i) => i.id)) ?? []);
        set((s) => ({
          collections: s.collections.filter((c) => c.id !== id),
          links: pruneLinksForItems(s.links, removedItemIds)
        }));
        void persist();
        saveTabLinks(get().links);
      },
      renameCollection(id, name) {
        set((s) => ({
          collections: s.collections.map(
            (c) => c.id === id ? { ...c, name, updatedAt: Date.now() } : c
          )
        }));
        void persist();
      },
      addItem(collectionId, item) {
        const newItem = {
          ...item,
          id: randomId(),
          createdAt: Date.now(),
          updatedAt: Date.now()
        };
        set((s) => ({
          collections: s.collections.map(
            (c) => c.id === collectionId ? { ...c, items: [...c.items, newItem], updatedAt: Date.now() } : c
          )
        }));
        void persist();
        return newItem;
      },
      updateItem(collectionId, itemId, updates) {
        set((s) => ({
          collections: s.collections.map(
            (c) => c.id === collectionId ? {
              ...c,
              updatedAt: Date.now(),
              items: c.items.map(
                (i) => i.id === itemId ? { ...i, ...updates, updatedAt: Date.now() } : i
              )
            } : c
          )
        }));
        void persist();
      },
      deleteItem(collectionId, itemId) {
        set((s) => ({
          collections: s.collections.map(
            (c) => c.id === collectionId ? {
              ...c,
              items: c.items.filter((i) => i.id !== itemId),
              updatedAt: Date.now()
            } : c
          ),
          links: pruneLinksForItems(s.links, /* @__PURE__ */ new Set([itemId]))
        }));
        void persist();
        saveTabLinks(get().links);
      },
      moveItem(fromCollectionId, fromIndex, toCollectionId, toIndex) {
        const { collections } = get();
        const fromCollection = collections.find((c) => c.id === fromCollectionId);
        if (!fromCollection || fromIndex < 0 || fromIndex >= fromCollection.items.length) {
          return;
        }
        const item = fromCollection.items[fromIndex];
        if (!item) {
          return;
        }
        const newCollections = collections.map((c) => {
          if (c.id === fromCollectionId && c.id === toCollectionId) {
            const items = [...c.items];
            items.splice(fromIndex, 1);
            items.splice(toIndex, 0, item);
            return { ...c, items, updatedAt: Date.now() };
          }
          if (c.id === fromCollectionId) {
            return {
              ...c,
              items: c.items.filter((_, i) => i !== fromIndex),
              updatedAt: Date.now()
            };
          }
          if (c.id === toCollectionId) {
            const items = [...c.items];
            items.splice(toIndex, 0, item);
            return { ...c, items, updatedAt: Date.now() };
          }
          return c;
        });
        set({ collections: newCollections });
        void persist();
      },
      analyzeImport(json) {
        const empty = {
          ok: false,
          newCollections: [],
          newItems: [],
          changedItems: [],
          unchangedCount: 0,
          _incoming: []
        };
        const CollectionItemSchema = z.object({
          id: z.string(),
          name: z.string(),
          query: z.string(),
          variables: z.string().optional(),
          headers: z.string().optional(),
          description: z.string().optional(),
          createdAt: z.number(),
          updatedAt: z.number()
        });
        const CollectionSchema = z.object({
          id: z.string(),
          name: z.string(),
          description: z.string().optional(),
          createdAt: z.number(),
          updatedAt: z.number(),
          items: z.array(CollectionItemSchema)
        });
        const EnvelopeSchema = z.union([
          z.object({
            version: z.number().optional(),
            collections: z.array(CollectionSchema)
          }),
          z.array(CollectionSchema)
        ]);
        let parsed;
        try {
          parsed = JSON.parse(json);
        } catch {
          return empty;
        }
        const result = EnvelopeSchema.safeParse(parsed);
        if (!result.success) {
          return empty;
        }
        const incoming = Array.isArray(result.data) ? result.data : result.data.collections;
        if (incoming.length === 0) {
          return empty;
        }
        const localCollections = get().collections;
        const localItemMap = /* @__PURE__ */ new Map();
        for (const col of localCollections) {
          for (const item of col.items) {
            localItemMap.set(item.id, { item, collectionId: col.id });
          }
        }
        const localCollectionIds = new Set(localCollections.map((c) => c.id));
        const newCollections = [];
        const newItems = [];
        const changedItems = [];
        let unchangedCount = 0;
        for (const incomingCol of incoming) {
          const genuinelyNewItems = incomingCol.items.filter(
            (item) => !localItemMap.has(item.id)
          );
          if (!localCollectionIds.has(incomingCol.id) && (incomingCol.items.length === 0 || genuinelyNewItems.length > 0)) {
            newCollections.push({ ...incomingCol, items: [] });
          }
          for (const incomingItem of incomingCol.items) {
            const local = localItemMap.get(incomingItem.id);
            if (!local) {
              newItems.push({
                item: incomingItem,
                // Land in the incoming parent; if that parent is new it will be
                // created during apply, otherwise it already exists locally.
                targetCollectionId: incomingCol.id
              });
            } else if (itemContentEqual(incomingItem, local.item)) {
              unchangedCount++;
            } else {
              changedItems.push({
                incoming: incomingItem,
                current: local.item,
                // Use where the recipient actually keeps it, not the incoming parent.
                currentCollectionId: local.collectionId
              });
            }
          }
        }
        return {
          ok: true,
          newCollections,
          newItems,
          changedItems,
          unchangedCount,
          _incoming: incoming
        };
      },
      applyImport(analysis, resolution) {
        if (!analysis.ok) {
          return;
        }
        if (resolution.mode === "replace") {
          set({ collections: analysis._incoming });
          void persist();
          return;
        }
        const now = Date.now();
        let collections = [...get().collections];
        for (const shell of analysis.newCollections) {
          collections.push({ ...shell, items: [] });
        }
        for (const { item, targetCollectionId } of analysis.newItems) {
          collections = collections.map(
            (c) => c.id === targetCollectionId ? { ...c, items: [...c.items, item], updatedAt: now } : c
          );
        }
        const shouldApply = (itemId) => {
          if ("applyChanges" in resolution) {
            return resolution.applyChanges;
          }
          return resolution.changedItemIds.has(itemId);
        };
        for (const conflict of analysis.changedItems) {
          if (!shouldApply(conflict.incoming.id)) {
            continue;
          }
          collections = collections.map((c) => {
            if (c.id !== conflict.currentCollectionId) {
              return c;
            }
            return {
              ...c,
              updatedAt: now,
              items: c.items.map(
                (i) => i.id === conflict.incoming.id ? {
                  // Replace content fields; keep the local createdAt.
                  ...conflict.incoming,
                  createdAt: conflict.current.createdAt,
                  updatedAt: now
                } : i
              )
            };
          });
        }
        set({ collections });
        void persist();
      },
      exportCollections() {
        return JSON.stringify(
          { version: 1, collections: get().collections },
          null,
          2
        );
      },
      exportCollection(id) {
        const collection = get().collections.find((c) => c.id === id);
        return JSON.stringify(
          { version: 1, collections: collection ? [collection] : [] },
          null,
          2
        );
      },
      exportItem(itemId) {
        for (const col of get().collections) {
          const item = col.items.find((i) => i.id === itemId);
          if (item) {
            const shell = {
              id: col.id,
              name: col.name,
              description: col.description,
              createdAt: col.createdAt,
              updatedAt: col.updatedAt,
              items: [item]
            };
            return JSON.stringify(
              { version: 1, collections: [shell] },
              null,
              2
            );
          }
        }
        return JSON.stringify({ version: 1, collections: [] }, null, 2);
      },
      linkTab(tabId, collectionId, itemId) {
        set((s) => ({
          // An item lives in exactly one tab: drop any stale link to it (e.g. a
          // closed tab's) before recording the new one.
          links: {
            ...pruneLinksForItems(s.links, /* @__PURE__ */ new Set([itemId])),
            [tabId]: { collectionId, itemId }
          }
        }));
        saveTabLinks(get().links);
      },
      requestSave(operation) {
        if (get().config.readOnly) {
          return false;
        }
        const { links, collections, actions } = get();
        const link = operation.id ? links[operation.id] : void 0;
        if (link) {
          const collection = collections.find((c) => c.id === link.collectionId);
          const item = collection == null ? void 0 : collection.items.find((i) => i.id === link.itemId);
          if (item) {
            actions.updateItem(link.collectionId, link.itemId, {
              query: operation.query ?? "",
              variables: operation.variables ?? "",
              headers: operation.headers ?? ""
            });
            return true;
          }
        }
        actions.openSaveDialog({
          tabId: operation.id,
          query: operation.query ?? "",
          variables: operation.variables ?? "",
          headers: operation.headers ?? "",
          name: deriveOperationName(
            operation.query ?? "",
            operation.operationName
          )
        });
        return false;
      },
      openSaveDialog(input) {
        set({ saveDialog: { ...input, open: true } });
      },
      closeSaveDialog() {
        set((s) => ({ saveDialog: { ...s.saveDialog, open: false } }));
      }
    }
  };
});
const useCollectionsStore = createBoundedUseStore(collectionsStore);
export {
  collectionsStore,
  deriveOperationName,
  useCollectionsStore
};
//# sourceMappingURL=store.js.map
